import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert,
} from '@mui/material';
import { useModel } from '../../context/ModelContext';
import {
  preprocessImage,
  runFundusInference,
  runEyesInference,
  runDRDMEInference,
} from '../../utils/imageProcessing';
import {
  parsePathsFromFile,
  fetchImage,
  buildResultsCSV,
  buildResultsExcel,
} from '../../utils/mlTestingUtils';

export default function MLTestingPage() {
  const {
    loadModel,
    loadFundusModel,
    loadEyesModel,
    isLoading,
    isFundusLoading,
    isEyesLoading,
    error: modelError,
  } = useModel();

  const [file, setFile] = useState(null);
  const [paths, setPaths] = useState([]);
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPaths([]);
    setResults([]);
    setError(null);
    parsePathsFromFile(f)
      .then((parsed) => setPaths(parsed))
      .catch((err) => {
        setError(err.message || 'Failed to parse file');
      });
  };

  const runBatch = useCallback(async () => {
    if (!paths.length) {
      setError('Upload a CSV or Excel file with S3 paths first.');
      return;
    }
    setError(null);
    setRunning(true);
    setResults([]);

    let drdmeSess, fundusSess, eyesSess;
    try {
      drdmeSess = await loadModel();
      fundusSess = await loadFundusModel();
      eyesSess = await loadEyesModel();
    } catch (err) {
      setError(err.message || 'Failed to load models');
      setRunning(false);
      return;
    }
    if (!drdmeSess || !fundusSess || !eyesSess) {
      setError('Models not ready. Try again.');
      setRunning(false);
      return;
    }

    const out = [];
    const total = paths.length;
    const safeSetProgress = (p) => {
      try {
        setProgress(p);
      } catch (_) {
        /* ignore state update errors */
      }
    };
    const safeSetResults = (next) => {
      try {
        setResults(next);
      } catch (_) {
        /* ignore so loop continues; results still in out */
      }
    };

    try {
      for (let i = 0; i < paths.length; i++) {
        safeSetProgress(Math.round((i / total) * 100));
        const pathStr = paths[i];
        try {
          const imageFile = await fetchImage(pathStr);
          const prep = await preprocessImage(imageFile, { skipPreviewBlobs: true });
          const tensor = prep.normalizedData;
          // Run models sequentially to avoid ONNX "Session already started" errors
          const fundusResult = await runFundusInference(tensor, fundusSess);
          const eyesResult = await runEyesInference(tensor, eyesSess);
          const drdmeResult = await runDRDMEInference(tensor, drdmeSess);
          out.push({
            path: pathStr,
            fundus: fundusResult,
            eyes: eyesResult,
            drdme: drdmeResult,
          });
        } catch (err) {
          out.push({ path: pathStr, error: err.message || 'Inference failed' });
        }
        safeSetResults([...out]);
      }
    } finally {
      safeSetProgress(100);
      setRunning(false);
      setResults([...out]);
    }
  }, [paths, loadModel, loadFundusModel, loadEyesModel]);

  const downloadCSV = () => {
    if (!results.length) return;
    const csv = buildResultsCSV(results);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ml-testing-results-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadExcel = () => {
    if (!results.length) return;
    const blob = buildResultsExcel(results);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ml-testing-results-${Date.now()}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const modelsLoading = isLoading || isFundusLoading || isEyesLoading;

  const helpText =
    'Upload a CSV or Excel with a column of S3 object keys (e.g. media/uuid/left/file.png). Full https://…amazonaws.com/… URLs in the sheet are converted to keys before calling the server; passing the raw URL as the key breaks signing. Optional: set REACT_APP_ML_TESTING_DEFAULT_BUCKET if rows use bucket/key. Each image is loaded via a server presigned URL. Only rows whose path ends with an image extension (.jpg, .jpeg, .png, .webp, .bmp, .gif) are processed.';

  return (
    <Box sx={{ p: 2, maxWidth: '100%', overflow: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        ML Testing – Batch inference from S3 paths
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {helpText}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <Button variant="contained" component="label">
          Upload CSV / Excel
          <input type="file" accept=".csv,.xlsx,.xls" hidden onChange={handleFileChange} />
        </Button>
        {file && (
          <Typography variant="body2">
            {file.name} – {paths.length} path(s)
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          disabled={!paths.length || running || modelsLoading}
          onClick={runBatch}
        >
          {running ? 'Running…' : modelsLoading ? 'Loading models…' : 'Run models on all'}
        </Button>
        {results.length > 0 && (
          <>
            <Button variant="outlined" onClick={downloadCSV}>
              Download CSV
            </Button>
            <Button variant="outlined" onClick={downloadExcel}>
              Download Excel
            </Button>
          </>
        )}
      </Box>

      {(modelError || error) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {modelError || error}
        </Alert>
      )}

      {running && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={progress} sx={{ mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            {`Processed ${results.length} / ${paths.length}`}
          </Typography>
        </Box>
      )}

      {results.length > 0 && (
        <TableContainer component={Paper} sx={{ maxHeight: 440, mb: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>S3 path</TableCell>
                <TableCell>Fundus label</TableCell>
                <TableCell>Fundus prob (non)</TableCell>
                <TableCell>Fundus prob (fundus)</TableCell>
                <TableCell>Fundus logit (non)</TableCell>
                <TableCell>Fundus logit (fundus)</TableCell>
                <TableCell>Eyes label</TableCell>
                <TableCell>Eyes prob (left)</TableCell>
                <TableCell>Eyes prob (right)</TableCell>
                <TableCell>Eyes logit (left)</TableCell>
                <TableCell>Eyes logit (right)</TableCell>
                <TableCell>DR grade</TableCell>
                <TableCell>DR score</TableCell>
                <TableCell>DME grade</TableCell>
                <TableCell>DME score</TableCell>
                <TableCell>Mahal.</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((r, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.path}
                  </TableCell>
                  <TableCell>{r.fundus?.fundus_label ?? '-'}</TableCell>
                  <TableCell>
                    {r.fundus?.fundus_score?.[0] != null ? Number(r.fundus.fundus_score[0]).toFixed(10) : '-'}
                  </TableCell>
                  <TableCell>
                    {r.fundus?.fundus_score?.[1] != null ? Number(r.fundus.fundus_score[1]).toFixed(10) : '-'}
                  </TableCell>
                  <TableCell>
                    {r.fundus?.fundus_logits?.[0] != null ? Number(r.fundus.fundus_logits[0]).toFixed(10) : '-'}
                  </TableCell>
                  <TableCell>
                    {r.fundus?.fundus_logits?.[1] != null ? Number(r.fundus.fundus_logits[1]).toFixed(10) : '-'}
                  </TableCell>
                  <TableCell>{r.eyes?.eyes_label ?? '-'}</TableCell>
                  <TableCell>
                    {r.eyes?.eyes_score?.[0] != null ? Number(r.eyes.eyes_score[0]).toFixed(10) : '-'}
                  </TableCell>
                  <TableCell>
                    {r.eyes?.eyes_score?.[1] != null ? Number(r.eyes.eyes_score[1]).toFixed(10) : '-'}
                  </TableCell>
                  <TableCell>
                    {r.eyes?.eyes_logits?.[0] != null ? Number(r.eyes.eyes_logits[0]).toFixed(10) : '-'}
                  </TableCell>
                  <TableCell>
                    {r.eyes?.eyes_logits?.[1] != null ? Number(r.eyes.eyes_logits[1]).toFixed(10) : '-'}
                  </TableCell>
                  <TableCell>{r.drdme?.DR_grade ?? '-'}</TableCell>
                  <TableCell>{r.drdme?.DR_score != null ? Number(r.drdme.DR_score).toFixed(10) : '-'}</TableCell>
                  <TableCell>{r.drdme?.DME_grade ?? '-'}</TableCell>
                  <TableCell>{r.drdme?.DME_score != null ? Number(r.drdme.DME_score).toFixed(10) : '-'}</TableCell>
                  <TableCell>
                    {r.drdme?.mahalanobis_distance != null ? Number(r.drdme.mahalanobis_distance).toFixed(6) : '-'}
                  </TableCell>
                  <TableCell sx={{ color: 'error.main' }}>{r.error ?? '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
